import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut, getMultiFactorResolver, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { TENANT_COMPANY_NAME, TENANT_ID, isPublicBrandSlug } from '../tenant';
import { ACCOUNT_BLOCK_KEY, companyTenantBlockReason, isSuperAdminClaims } from '../session';
import PulseLogo from '../components/PulseLogo';
import PulseItGroupFooter from '../components/PulseItGroupFooter';
import { useI18n, usePublicTenantLocale } from '../i18n';
import { moduleOn, useDirectoryFeatures } from '../tenantFeatures';

function Login() {
    const { slug } = useParams();
    const tenantSlug = TENANT_ID;
    const { t } = useI18n();
    usePublicTenantLocale(tenantSlug);
    const features = useDirectoryFeatures(tenantSlug);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [smsCode, setSmsCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isMfaRequired, setIsMfaRequired] = useState(false);
    const [mfaResolver, setMfaResolver] = useState(null);
    const [verificationId, setVerificationId] = useState(null);
    const [company, setCompany] = useState({ companyName: TENANT_COMPANY_NAME, logoUrl: '' });
    const navigate = useNavigate();

    useEffect(() => {
        try {
            const blocked = sessionStorage.getItem(ACCOUNT_BLOCK_KEY);
            if (blocked) {
                setError(blocked);
                sessionStorage.removeItem(ACCOUNT_BLOCK_KEY);
            }
        } catch (e) { /* ignore */ }
        getDoc(doc(db, 'directory', TENANT_ID)).then((snap) => {
            if (snap.exists()) {
                const row = snap.data();
                setCompany({ companyName: TENANT_COMPANY_NAME, logoUrl: row.logoUrl || '' });
            }
        }).catch(() => {});
    }, [tenantSlug]);

    const routeUser = async (user) => {
        const token = await user.getIdTokenResult(true);
        if (isSuperAdminClaims(token.claims)) {
            await signOut(auth);
            setError('Pulse super admin uses a separate portal: /super-admin/login');
            setLoading(false);
            return;
        }
        const blocked = await companyTenantBlockReason(token.claims.tenantId);
        if (blocked) {
            await signOut(auth);
            setError(blocked);
            setLoading(false);
            return;
        }
        navigate('/dashboard');
    };

    const handleLogin = async (e) => {
        e.preventDefault(); setLoading(true); setError('');
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            await routeUser(userCredential.user);
        } catch (err) {
            if (err.code === 'auth/multi-factor-auth-required') {
                window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { 'size': 'invisible' });
                const resolver = getMultiFactorResolver(auth, err);
                const phoneAuthProvider = new PhoneAuthProvider(auth);
                const vId = await phoneAuthProvider.verifyPhoneNumber({ multiFactorHint: resolver.hints, session: resolver.session }, window.recaptchaVerifier);
                setMfaResolver(resolver); setVerificationId(vId); setIsMfaRequired(true);
            } else if (err.code === 'auth/user-disabled') {
                setError(t('login.disabled'));
            } else {
                setError(err.message);
            }
            setLoading(false);
        }
    };

    const handleMfaVerify = async (e) => {
        e.preventDefault(); setLoading(true); setError('');
        try {
            const cred = PhoneAuthProvider.credential(verificationId, smsCode);
            const assertion = PhoneMultiFactorGenerator.assertion(cred);
            const userCredential = await mfaResolver.resolveSignIn(assertion);
            await routeUser(userCredential.user);
        } catch (err) {
            setError(err.message); setLoading(false);
        }
    };

    return (
        <div className="container-fluid min-vh-100 d-flex flex-column" style={{ background: '#f0f2f5' }}>
            <div id="recaptcha-container"></div>
            <div className="flex-grow-1 d-flex align-items-center">
            <div className="row justify-content-center w-100">
                <div className="col-md-4">
                    <div className="card shadow-lg border-0 rounded-3">
                        <div className="card-body p-5">
                            <div className="text-center mb-4">
                                <PulseLogo size="medium" showTagline={false} companyName={company.companyName} logoUrl={company.logoUrl} />
                            </div>
                            <h5 className="text-center text-muted mb-1">{isMfaRequired ? t('login.mfaTitle') : t('login.title')}</h5>
                            <p className="text-center small text-muted mb-2">{t('login.blurb', { company: company.companyName })}</p>
                            <p className="text-center small text-muted mb-4">{t('login.demoEmails')}</p>
                            {error && <div className="alert alert-danger text-xs">{error}</div>}

                            {!isMfaRequired ? (
                                <form onSubmit={handleLogin}>
                                    <div className="mb-3">
                                        <label className="form-label font-semibold text-xs">{t('common.email')}</label>
                                        <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label font-semibold text-xs">{t('common.password')}</label>
                                        <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} required />
                                    </div>
                                    <button type="submit" className="btn btn-primary w-100 font-bold" disabled={loading}>{loading ? t('login.verifying') : t('login.signIn')}</button>
                                </form>
                            ) : (
                                <form onSubmit={handleMfaVerify}>
                                    <input type="text" maxLength="6" className="form-control text-center font-mono tracking-widest fw-bold mb-3" value={smsCode} onChange={(e) => setSmsCode(e.target.value)} placeholder="000000" required />
                                    <button type="submit" className="btn btn-success w-100 font-bold">{loading ? t('login.authorizing') : t('login.verifyPin')}</button>
                                </form>
                            )}
                            {moduleOn(features, 'clientPortal') && (
                                <div className="text-center mt-3">
                                    <Link to={slug && isPublicBrandSlug(slug) ? '/c/' + String(slug).trim().toLowerCase() + '/client' : '/client/login'} className="small d-block">{t('login.policyholderPortal')}</Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            </div>
            <PulseItGroupFooter />
        </div>
    );
}
export default Login;
